export const normalizeBooleanField = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();

    if (["true", "1", "yes", "si", "sí"].includes(normalized)) {
      return true;
    }

    if (["false", "0", "no"].includes(normalized)) {
      return false;
    }
  }

  return fallback;
};

/**
 * Identificador del texto legal de una pregunta `terms`. Se guarda tal cual lo
 * elige el administrador en el selector de políticas (el `type` exacto de la
 * política); si no se indica ninguno, se usa `Terms` (retrocompatible).
 */
export const resolveLegalTextKey = (value) => {
  const normalized = typeof value === "string" ? value.trim() : "";
  return normalized === "" ? "Terms" : normalized;
};

const stripHtmlTags = (value) => {
  if (typeof value !== "string") return "";
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

// Longitud máxima por defecto (caracteres) del texto de la política mostrado en
// el selector de la pregunta `terms`. Se puede sobrescribir con la variable de
// entorno `VITE_LEGAL_TEXT_TITLE_MAX_LENGTH` del `.env`.
export const DEFAULT_LEGAL_TEXT_TITLE_MAX_LENGTH = 100;

/**
 * Resuelve la longitud máxima del texto de la política a partir del valor de la
 * variable de entorno `VITE_LEGAL_TEXT_TITLE_MAX_LENGTH`. Si no está definida,
 * no es un entero positivo o no es numérica, se usa el valor por defecto (100).
 */
export const resolveLegalTextTitleMaxLength = (
  rawValue = import.meta.env.VITE_LEGAL_TEXT_TITLE_MAX_LENGTH,
) => {
  const parsed = Number.parseInt(rawValue, 10);
  return Number.isFinite(parsed) && parsed > 0
    ? parsed
    : DEFAULT_LEGAL_TEXT_TITLE_MAX_LENGTH;
};

export const LEGAL_TEXT_TITLE_MAX_LENGTH = resolveLegalTextTitleMaxLength();

const truncateText = (value, maxLength = LEGAL_TEXT_TITLE_MAX_LENGTH) => {
  if (typeof value !== "string") return "";
  const characters = Array.from(value);
  if (characters.length <= maxLength) return value;
  return `${characters.slice(0, maxLength).join("").trimEnd()}...`;
};

const resolveLegalTextTitle = (policy, language) => {
  if (!Array.isArray(policy?.preTexts)) return "";
  const localized = policy.preTexts.find(
    (text) => text?.language === language && typeof text?.text === "string",
  );
  const fallback = policy.preTexts.find(
    (text) => typeof text?.text === "string" && text.text.trim() !== "",
  );
  return truncateText(stripHtmlTags(localized?.text ?? fallback?.text));
};

/**
 * Opciones del selector de políticas para el campo "Texto legal asociado" de una
 * pregunta `terms`. A partir de las políticas del tenant (`configurable_texts`)
 * construye una opción por cada `type` distinto, con el título en el idioma
 * activo (`Título (Type)`) como etiqueta. Garantiza que siempre exista la opción
 * `Terms` y que el valor actual esté presente aunque no corresponda a ninguna
 * política (datos antiguos).
 */
export const buildLegalTextOptions = (policies, currentValue, language) => {
  const list = Array.isArray(policies)
    ? policies
    : Array.isArray(policies?.data)
      ? policies.data
      : [];

  const seen = new Set();
  const options = [];
  const push = (value, label) => {
    const normalizedValue = typeof value === "string" ? value.trim() : "";
    if (normalizedValue === "" || seen.has(normalizedValue)) return;
    seen.add(normalizedValue);
    options.push({ value: normalizedValue, label: label || normalizedValue });
  };

  for (const policy of list) {
    const type = typeof policy?.type === "string" ? policy.type.trim() : "";
    if (type === "") continue;
    const title = resolveLegalTextTitle(policy, language);
    push(type, title ? `${title} (${type})` : type);
  }

  push("Terms", "Terms");
  push(currentValue, typeof currentValue === "string" ? currentValue : "");

  return options;
};

export const createQuestionPayload = ({
  values,
  selectedLanguage,
  coerceOptionText,
}) => {
  const processedOptions =
    values.optionsText && values.optionsText[selectedLanguage]
      ? values.optionsText[selectedLanguage].map((option, index) => {
          const optionText = coerceOptionText(option);
          return {
            text: {
              language: selectedLanguage,
              text: optionText,
            },
            value: index.toString(),
            position: index,
          };
        })
      : [];

  return {
    texts: {
      language: selectedLanguage,
      text: values.texts[selectedLanguage] || "",
    },    descriptions: {
      language: selectedLanguage,
      text: values.descriptions[selectedLanguage] || "",
    },
    placeholders: {
      language: selectedLanguage,
      text: values.placeholders[selectedLanguage] || "",
    },
    order: values.position,
    type: values.type,
    ...(values.type === "terms"
      ? {
          legalTextKey: resolveLegalTextKey(values.legalTextKey),
        }
      : {}),
    active: normalizeBooleanField(values.active, true),
    required: normalizeBooleanField(values.required),
    anonymizable: normalizeBooleanField(values.anonymizable),
    visibility: {
      form: true,
    },
    depends_id: values.depends_id || null,
    related_question: values.related_question ?? null,
    options: processedOptions,
  };
};

export const getUpdateQuestionDefaultValues = (questionToUpdateData) => ({
  texts: (questionToUpdateData.texts || []).reduce((acc, text) => {
    acc[text.language] = {
      text: text.text,
      id: text.id,
    };
    return acc;
  }, {}),
  descriptions: (questionToUpdateData.descriptions || []).reduce((acc, desc) => {
    acc[desc.language] = {
      text: desc.text,
      id: desc.id,
    };
    return acc;
  }, {}),
  placeholders: (questionToUpdateData.placeholders || []).reduce(
    (acc, placeholder) => {
      acc[placeholder.language] = {
        text: placeholder.text,
        id: placeholder.id,
      };
      return acc;
    },
    {},
  ),
  screen: questionToUpdateData.screen ?? null,
  type: questionToUpdateData.type || "",
  optionsText: (questionToUpdateData.options || []).reduce((acc, option) => {
    for (const text of option.text || []) {
      if (!acc[text.language]) {
        acc[text.language] = [];
      }
      acc[text.language].push({
        text: text.text,
        id: text.id,
        optionId: option.id,
      });
    }
    return acc;
  }, {}),
  active: normalizeBooleanField(questionToUpdateData.active),
  required: normalizeBooleanField(questionToUpdateData.required),
  anonymizable: normalizeBooleanField(questionToUpdateData.anonymizable),
  legalTextKey: questionToUpdateData.legalTextKey || "Terms",
  depends_id: questionToUpdateData.depends ? questionToUpdateData.depends.id : null,
  related_question: null,
});

export const updateQuestionPayload = ({
  values,
  questionToUpdateData,
  selectedLanguage,
}) => {
  const formattedTexts = [
    {
      language: selectedLanguage,
      text: values.texts[selectedLanguage]?.text || "",
      ...(values.texts[selectedLanguage]?.id
        ? { id: values.texts[selectedLanguage].id }
        : {}),
    },
  ];

  const optionsToSend = [];
  const currentLangOptions =
    values.optionsText && values.optionsText[selectedLanguage]
      ? values.optionsText[selectedLanguage]
      : [];

  currentLangOptions.forEach((option, index) => {
    optionsToSend.push({
      ...(option.optionId ? { id: option.optionId } : {}),
      text: [
        {
          language: selectedLanguage,
          text: option.text || "",
          ...(option.id ? { id: option.id } : {}),
        },
      ],
      value: index.toString(),
      position: index,
    });
  });

  return {
    form_id: questionToUpdateData.form.id,
    texts: formattedTexts,
    descriptions: [
      {
        language: selectedLanguage,
        text: values.descriptions[selectedLanguage]?.text || "",
        ...(values.descriptions[selectedLanguage]?.id
          ? { id: values.descriptions[selectedLanguage].id }
          : {}),
      },
    ],
    placeholders: [
      {
        language: selectedLanguage,
        text: values.placeholders[selectedLanguage]?.text || "",
        ...(values.placeholders[selectedLanguage]?.id
          ? { id: values.placeholders[selectedLanguage].id }
          : {}),
      },
    ],
    options: optionsToSend,
    type: values.type,
    ...(values.type === "terms"
      ? {
          legalTextKey: resolveLegalTextKey(values.legalTextKey),
        }
      : {}),
    screen: values.screen,
    active: normalizeBooleanField(values.active),
    required: normalizeBooleanField(values.required),
    anonymizable: normalizeBooleanField(values.anonymizable),
    depends_id: values.depends_id,
    related_question: values.related_question ?? null,
  };
};
